import { type FC, type FormEvent, useCallback, useState } from "react";

import type { DashboardOrganizationSummary } from "../../../dashboard-client";

import { Alert } from "../../common/Alert";
import { Button } from "../../common/Button";
import { FormInput } from "../../common/forms/FormInput";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface CreateOrgStepProps {
  organizations: DashboardOrganizationSummary[];
  onSubmit: (name: string, slug: string) => Promise<void>;
  onSelectExisting: (orgId: string) => Promise<void>;
  submitting: boolean;
  error: string | null;
}

export const CreateOrgStep: FC<CreateOrgStepProps> = ({
  organizations,
  onSubmit,
  onSelectExisting,
  submitting,
  error,
}) => {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(
    organizations.length === 0,
  );

  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      if (!slugTouched) {
        setSlug(slugify(value));
      }
    },
    [slugTouched],
  );

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (submitting) {
        return;
      }
      void onSubmit(name.trim(), slug.trim());
    },
    [name, slug, submitting, onSubmit],
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-mono text-sm font-bold uppercase tracking-widest text-white">
          Create your organization
        </h2>
        <p className="mt-1 text-sm leading-6 text-slate-400">
          An organization groups your repositories, integrations, and team
          members together.
        </p>
      </div>

      {organizations.length > 0 && !showCreateForm && (
        <div className="space-y-3">
          <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
            Use an existing organization
          </p>
          {organizations.map((org) => (
            <button
              key={org.organization_id}
              type="button"
              disabled={submitting}
              onClick={() => void onSelectExisting(org.organization_id)}
              className="flex w-full cursor-pointer items-center justify-between border border-slate-700 bg-arti-background p-3 text-left transition-colors hover:border-emerald-500/50 hover:bg-emerald-500/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <div>
                <span className="font-mono text-sm font-bold text-white">
                  {org.name}
                </span>
                <span className="ml-2 font-mono text-[10px] text-slate-500">
                  {org.slug}
                </span>
              </div>
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">
                Select
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowCreateForm(true)}
            className="cursor-pointer font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400 transition-colors hover:text-emerald-300"
          >
            + Create a new organization
          </button>
        </div>
      )}

      {(showCreateForm || organizations.length === 0) && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Organization Name"
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="My Team"
            required
          />
          <FormInput
            label="Slug"
            type="text"
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(slugify(e.target.value));
            }}
            placeholder="my-team"
            required
          />
          <p className="!mt-1 font-mono text-[10px] text-slate-500">
            Used in URLs. Lowercase letters, numbers, and hyphens only.
          </p>

          {error && (
            <Alert variant="error" animate>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            loading={submitting}
            loadingText="Creating..."
            fullWidth
          >
            Create Organization
          </Button>

          {organizations.length > 0 && (
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="cursor-pointer font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-300"
            >
              Back to existing organizations
            </button>
          )}
        </form>
      )}
    </div>
  );
};
